import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, RefreshCw, Shield, Clock, Calculator, TrendingUp, CheckCircle, Users, ArrowLeft } from 'lucide-react';
import { toast as sonnerToast } from 'sonner';
import { useToast } from '@/hooks/use-toast';
import { getApiMessage } from '@/lib/apiMessage';

export default function ModernOTPVerification() {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // We’ll use toasts for success feedback instead of inline success banners
  const [countdown, setCountdown] = useState(0);
  const [partnerBranding, setPartnerBranding] = useState<any>(null);
  const { completeOtp, resendOtp, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Detect partner branding based on subdomain
  useEffect(() => {
    const hostname = window.location.hostname;
    
    // Direct subdomain to partner branding mapping
    const subdomainToPartnerBranding: { [key: string]: any } = {
      'tobelmann.billportal.io': {
        id: '5b9cddb9-9416-498b-bf75-c1f555c251fc',
        name: 'Tobelmann',
        brand_logo_url: 'https://tobelmann.billportal.io/uploads/partner-logos/processed_5b9cddb9-9416-498b-bf75-c1f555c251fc_1758572292764.png',
        brand_primary_color: '#1e40af',
        brand_secondary_color: '#3b82f6'
      }
      // Add more partner mappings as needed
    };

    if (subdomainToPartnerBranding[hostname]) {
      setPartnerBranding(subdomainToPartnerBranding[hostname]);
    }
  }, []);

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Start countdown when component mounts
  useEffect(() => {
    setCountdown(60); // 60 seconds cooldown
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) {
      setError('Please enter the OTP');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const { error } = await completeOtp(otp.trim());
      if (!error) {
        // After successful OTP, navigate to the workspace selection page.
        // The selection page will query the workspaces and auto-redirect if there's only one.
        try { sessionStorage.removeItem('currentWorkspaceId'); } catch (_) {}
        try { sessionStorage.removeItem('workspaceData'); } catch (_) {}
        navigate('/select-workspace');
      } else {
        setError(error);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    setError(null);
    
    try {
      const res = await resendOtp();
      if (res.error) {
        const msg = typeof res.error === 'string' ? res.error : getApiMessage(res.error) || 'Failed to resend OTP';
        setError(msg);
      } else if (res.data?.success) {
        const msg = getApiMessage(res.data) || 'OTP has been resent to your email';
        setCountdown(60);
        setOtp('');
        // Show success via both app toast and sonner for maximum visibility
        try { toast({ title: 'Code sent', description: msg }); } catch (_) {}
        try { sonnerToast.success(msg); } catch (_) {}
      } else {
        const msg = getApiMessage(res.data) || res.data?.message || 'Failed to resend OTP';
        setError(msg);
      }
    } catch (err: any) {
      console.error('Resend OTP error:', err);
      const msg = getApiMessage(err) || err?.response?.data?.message || 'Failed to resend OTP. Please try again.';
      setError(msg);
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/auth');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex">
      {/* Left Section - Promotional Content (Same as Login) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-indigo-700 p-12 flex-col justify-center relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 bg-white rounded-full"></div>
          <div className="absolute bottom-32 right-16 w-24 h-24 bg-white rounded-full"></div>
          <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-white rounded-full"></div>
        </div>

        {/* Illustration */}
        <div className="relative z-10 mb-8">
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              {/* Person at desk */}
              <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center mb-4">
                <Users className="w-16 h-16 text-white" />
              </div>
              
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">%</span>
              </div>
              <div className="absolute -bottom-2 -left-2 w-10 h-10 bg-green-400 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div className="absolute top-8 -left-8 w-8 h-8 bg-blue-400 rounded-full flex items-center justify-center">
                <Calculator className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Headline */}
        <div className="relative z-10 mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            Secure verification for your account
          </h1>
          <p className="text-xl text-blue-100">
            We've sent a verification code to your email for secure access
          </p>
        </div>

        {/* Features List */}
        <div className="relative z-10 space-y-4 mb-8">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
            <span className="text-white text-lg">Secure two-factor authentication</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
            <span className="text-white text-lg">Quick and easy verification process</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
            <span className="text-white text-lg">Enhanced account security</span>
          </div>
        </div>

        {/* Branding */}
        <div className="relative z-10">
          <div className="flex items-center mb-4">
            {partnerBranding?.brand_logo_url ? (
              <div className="flex items-center space-x-4">
                <img
                  src={partnerBranding.brand_logo_url}
                  alt={`${partnerBranding.name} logo`}
                  className="h-16 w-auto max-w-[200px] object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
                <div className="hidden">
                  <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-2xl">BP</span>
                  </div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-white">{partnerBranding.name}</div>
                  <div className="text-blue-200 text-lg">Powered by BillPortal</div>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">BP</span>
                </div>
                <div>
                  <div className="text-6xl font-bold text-white">BillPortal</div>
                  <div className="text-blue-200 text-lg">from makers of advanced utility management</div>
                </div>
              </div>
            )}
          </div>
          <p className="text-blue-300 text-sm mt-4">
            {partnerBranding 
              ? `${partnerBranding.name} is powered by BillPortal - a comprehensive utility bill management platform.`
              : 'BillPortal is a comprehensive utility bill management platform designed for modern businesses.'
            }
          </p>
        </div>
      </div>

      {/* Right Section - OTP Verification Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Partner Logo in OTP Area */}
          {partnerBranding?.brand_logo_url && (
            <div className="text-center mb-6">
              <img
                src={partnerBranding.brand_logo_url}
                alt={`${partnerBranding.name} logo`}
                className="h-12 w-auto max-w-[150px] object-contain mx-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Welcome Message */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Verify Your Account</h2>
            <p className="text-gray-600">Enter the verification code sent to your email</p>
          </div>

          {/* Back to Login */}
          <div className="text-center mb-6">
            <button
              onClick={handleBackToLogin}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center justify-center mx-auto"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Login
            </button>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <Alert className="mb-6 border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          {/* success handled via toast; no inline success banner */}

          {/* OTP Form */}
          <form onSubmit={onSubmit} className="space-y-6">
            <div>
              <Label htmlFor="otp" className="text-sm font-medium text-gray-700">
                Verification Code
              </Label>
              <Input
                id="otp"
                type="text"
                placeholder="Enter 6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="mt-1 h-12 text-center text-2xl tracking-widest border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                maxLength={6}
                required
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Enter the 6-digit code sent to your email
              </p>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium"
              disabled={loading || otp.length !== 6}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify & Continue'
              )}
            </Button>
          </form>

          {/* automatic navigation handled after success */}

          {/* Resend OTP */}
          <div className="text-center mt-6">
            <p className="text-sm text-gray-500 mb-3">
              Didn't receive the code?
            </p>
            <Button
              variant="outline"
              onClick={handleResendOtp}
              disabled={resendLoading || countdown > 0}
              className="text-blue-600 border-blue-600 hover:bg-blue-50"
            >
              {resendLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : countdown > 0 ? (
                <>
                  <Clock className="w-4 h-4 mr-2" />
                  Resend in {countdown}s
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Resend Code
                </>
              )}
            </Button>
          </div>

          {/* Security Badges */}
          <div className="flex justify-center items-center space-x-4 mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <Shield className="w-4 h-4" />
              <span>SSL SECURE</span>
            </div>
            <div className="flex items-center space-x-1 text-xs text-gray-500">
              <Clock className="w-4 h-4" />
              <span>2FA ENABLED</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
